import React, { useState, useEffect, useRef } from "react";
import { collection, getDocs } from "firebase/firestore";
import { createLinkedConfiguration, newLinkedCreationAttempt } from './createLinkedConfiguration';
import { db } from "../utils/firebase";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { ChevronDown, Zap, FileText, Cpu, Layout } from "lucide-react";
import ModalPortal from "../utils/ModalPortal";
import { useAuth } from "../utils/authContext";
import { useGeminiModels, canSelectGeminiModel } from "./useGeminiModels";
import GeminiModelLabel from "./components/GeminiModelLabel";
import { sortByDisplayLabel } from "./sortByDisplayLabel";

// Prompt type options (matches AdminPromptBuilder)
const PROMPT_TYPE_OPTIONS = [
  { value: "document", label: "Document" },
  { value: "history", label: "History" },
  { value: "life-story", label: "Life Story" },
  { value: "system", label: "System" },
  { value: "table", label: "Table" },
];

// Section type options
const SECTION_TYPE_OPTIONS = [
  { value: "none", label: "None" },
  { value: "EditableTableSection", label: "Table Editor" },
  { value: "EditableTextSection", label: "Text Editor" },
];

// Processing type options
const PROCESSING_TYPE_OPTIONS = [
  { value: "none", label: "None" },
  { value: "submitToVertexAI", label: "AI Processing" },
  { value: "empty", label: "Empty (User Input Only)" },
];

/**
 * QuickAddDialog - Convenience dialog for creating Prompt + Step + Section together
 * All sections are optional, but at least one must be filled.
 */
const QuickAddDialog = ({ open, onClose, onSave }) => {
  const { currentUser } = useAuth();
  const { models, defaultModel, modelError, modelPolicyEnforced } = useGeminiModels();
  
  // Common
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const creationAttempt = useRef(null);
  const savingRef = useRef(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Prompt section
  const [promptExpanded, setPromptExpanded] = useState(true);
  const [promptText, setPromptText] = useState("");
  const [promptType, setPromptType] = useState("document");
  const [promptModel, setPromptModel] = useState("");
  
  useEffect(() => {
    if (defaultModel) setPromptModel(previous => previous || defaultModel);
  }, [defaultModel]);

  // AI Processing section
  const [processingExpanded, setProcessingExpanded] = useState(true);
  const [processingType, setProcessingType] = useState("submitToVertexAI");
  const [fieldSchemas, setFieldSchemas] = useState([]);
  const [selectedFieldSchema, setSelectedFieldSchema] = useState(null);
  const [loadingFieldSchemas, setLoadingFieldSchemas] = useState(false);
  
  // Section section
  const [sectionExpanded, setSectionExpanded] = useState(true);
  const [sectionType, setSectionType] = useState("EditableTextSection");
  
  // Track which sections have content
  const hasPromptContent = promptText.trim().length > 0;
  const hasProcessingContent = processingType !== "none";
  const hasSectionContent = sectionType !== "none";
  
  // Determine if prompt is filled - locks processing to AI Processing
  const promptFilled = hasPromptContent;
  
  // Load field schemas when processing type is "empty"
  const loadFieldSchemas = async () => {
    if (fieldSchemas.length > 0) return;
    setLoadingFieldSchemas(true);
    try {
      const snapshot = await getDocs(collection(db, "documentFieldSchemas"));
      const schemas = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        displayName: doc.data().displayName
      }));
      setFieldSchemas(sortByDisplayLabel(schemas, schema => schema.displayName || schema.name));
    } catch (err) {
      console.error("Error loading field schemas:", err);
    }
    setLoadingFieldSchemas(false);
  };
  
  // Reset form when dialog closes
  const handleClose = () => {
    creationAttempt.current = null;
    savingRef.current = false;
    setSaving(false);
    setName("");
    setPromptText("");
    setPromptType("document");
    setPromptModel(defaultModel);
    setProcessingType("submitToVertexAI");
    setSelectedFieldSchema(null);
    setSectionType("EditableTextSection");
    setPromptExpanded(true);
    setProcessingExpanded(true);
    setSectionExpanded(true);
    setError("");
    setSuccess("");
    onClose();
  };
  
  // Validate form
  const validateForm = () => {
    if (hasPromptContent && !canSelectGeminiModel(promptModel, models, modelPolicyEnforced)) {
      setError('Select an available AI model before saving.');
      return false;
    }
    if (!name.trim()) {
      setError("Please enter a name");
      return false;
    }
    
    // At least one section must have meaningful content
    const hasPrompt = promptText.trim().length > 0;
    const hasStep = processingType !== "none";
    const hasSection = sectionType !== "none";
    
    if (!hasPrompt && !hasStep && !hasSection) {
      setError("Please fill in at least one section (Prompt, AI Processing, or Document Section)");
      return false;
    }
    
    return true;
  };
  
  // Commit the requested group together; keep attempt IDs and inputs on failure.
  const handleSave = async () => {
    if (savingRef.current || !validateForm()) return;
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      creationAttempt.current ||= newLinkedCreationAttempt(db);
      const created = await createLinkedConfiguration(db, creationAttempt.current, {
        name,
        userId: currentUser?.uid || currentUser?.email || 'admin',
        prompt: promptText.trim() ? { text: promptText, type: promptType, model: promptModel } : null,
        processingType,
        sectionType,
        fieldSchema: processingType === 'empty' ? selectedFieldSchema : null,
        createDataField: processingType === 'submitToVertexAI',
        fieldCategory: 'Quick Add',
      });
      setSuccess(`Created: ${created.join(", ")}`);
      setTimeout(() => {
        handleClose();
        onSave?.();
      }, 1500);
    } catch (err) {
      console.error("Error in Quick Add:", err);
      setError(`Failed to create: ${err.message}`);
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <ModalPortal isOpen={open}>
      <Dialog 
        open={open} 
        onClose={() => !savingRef.current && handleClose()}
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { minHeight: 400 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Zap size={20} />
          Quick Add
        </DialogTitle>
        
        <DialogContent>
          {modelError && <Alert severity="error" sx={{ mb: 2 }}>{modelError}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          {/* Name field */}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
            sx={{ mb: 3 }}
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fill in at least one section below. Items will be linked automatically.
          </Typography>
          
          {/* Prompt Section */}
          <Accordion 
            expanded={promptExpanded} 
            onChange={(_, exp) => setPromptExpanded(exp)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ChevronDown size={18} />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FileText size={18} />
                <Typography>Prompt</Typography>
                {hasPromptContent && (
                  <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                    (has content)
                  </Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                label="Prompt Text"
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                multiline
                rows={4}
                fullWidth
                sx={{ mb: 2 }}
                placeholder="Enter the prompt instructions..."
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={promptType}
                    label="Type"
                    onChange={(e) => setPromptType(e.target.value)}
                  >
                    {PROMPT_TYPE_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={promptModel}
                    label="Model"
                    onChange={(e) => setPromptModel(e.target.value)}
                  >
                    {models.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}><GeminiModelLabel model={opt} /></MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </AccordionDetails>
          </Accordion>
          
          {/* AI Processing Section */}
          <Accordion 
            expanded={processingExpanded} 
            onChange={(_, exp) => setProcessingExpanded(exp)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ChevronDown size={18} />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Cpu size={18} />
                <Typography>AI Processing (Step)</Typography>
                {processingType !== "none" && (
                  <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                    ({PROCESSING_TYPE_OPTIONS.find(o => o.value === processingType)?.label})
                  </Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Processing Type</InputLabel>
                <Select
                  value={promptFilled ? "submitToVertexAI" : processingType}
                  label="Processing Type"
                  onChange={(e) => setProcessingType(e.target.value)}
                  disabled={promptFilled}
                >
                  {PROCESSING_TYPE_OPTIONS.filter(opt => !(promptFilled && opt.value === "none")).map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {promptFilled && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                  Locked to AI Processing because Prompt is filled.
                </Typography>
              )}
              
              {!promptFilled && processingType === "empty" && (
                <Autocomplete
                  options={fieldSchemas}
                  getOptionLabel={(opt) => opt.displayName || opt.name}
                  value={selectedFieldSchema}
                  onChange={(_, newValue) => setSelectedFieldSchema(newValue)}
                  onOpen={loadFieldSchemas}
                  loading={loadingFieldSchemas}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Section Schema (optional)"
                      size="small"
                      helperText="Link to an existing data structure"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingFieldSchemas ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            </AccordionDetails>
          </Accordion>
          
          {/* Document Section Section */}
          <Accordion 
            expanded={sectionExpanded} 
            onChange={(_, exp) => setSectionExpanded(exp)}
          >
            <AccordionSummary expandIcon={<ChevronDown size={18} />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Layout size={18} />
                <Typography>Document Section</Typography>
                {sectionType !== "none" && (
                  <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                    ({SECTION_TYPE_OPTIONS.find(o => o.value === sectionType)?.label})
                  </Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl fullWidth size="small">
                <InputLabel>Section Type</InputLabel>
                <Select
                  value={sectionType}
                  label="Section Type"
                  onChange={(e) => setSectionType(e.target.value)}
                >
                  {SECTION_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave} 
            disabled={saving || !name.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : <Zap size={16} />}
          >
            {saving ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </ModalPortal>
  );
};

export default QuickAddDialog;
